import { useState, ReactNode } from 'react'
import { Box, Button, Typography, Tooltip, IconButton } from '@mui/material'
import {
  FilterList as FilterListIcon,
  FilterAltOff as FilterAltOffIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  TableRows as TableIcon,
  ViewColumn as KanbanIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { SearchBar } from '../ui/SearchBar'
import { themeColors } from '../../app/layout/theme'
import {
  UnifiedFilterDialog,
  UnifiedFilters,
  FilterDef,
  countActiveFilters,
  EMPTY_UNIFIED_FILTERS,
} from './UnifiedFilterDialog'

export interface BulkAction {
  label: string
  onClick: () => void
  color?: 'primary' | 'error' | 'secondary'
}

interface ListToolbarProps {
  search: string
  onSearch: (value: string) => void
  searchPlaceholder?: string

  // Filter Props
  filterDefs?: FilterDef[]
  filters?: UnifiedFilters
  onFilterChange?: (filters: UnifiedFilters) => void
  /** Called when the filter dialog is opened — useful for triggering lazy data fetches */
  onFilterOpen?: () => void

  // View Mode Props
  viewMode?: 'table' | 'kanban'
  onViewModeChange?: (mode: 'table' | 'kanban') => void

  // Structured Selection Actions (rendered left of view mode buttons)
  selectedCount?: number
  bulkActions?: BulkAction[]
  onClearSelection?: () => void

  // Data refresh (rendered right of the Filters button)
  onRefresh?: () => void
  isRefreshing?: boolean

  // Extra Actions (e.g., New button)
  extraActions?: ReactNode

  // Left-aligned extra content (rendered left of the spacer)
  leftExtra?: ReactNode
}

export const ListToolbar = ({
  search,
  onSearch,
  searchPlaceholder = 'Search...',
  filterDefs = [],
  filters,
  onFilterChange,
  onFilterOpen,
  viewMode,
  onViewModeChange,
  selectedCount,
  bulkActions,
  onClearSelection,
  onRefresh,
  isRefreshing = false,
  extraActions,
  leftExtra,
}: ListToolbarProps) => {
  const [filterOpen, setFilterOpen] = useState(false)
  const activeCount = filters ? countActiveFilters(filters) : 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
      {/* Search Bar */}
      <SearchBar
        value={search}
        onSearch={onSearch}
        placeholder={searchPlaceholder}
        sx={{
          height: '38px',
          borderRadius: '8px',
          width: { xs: '100%', sm: 320 },
          '& .MuiInputBase-root': {
            height: '100%',
          }
        }}
      />

      {/* Unified Filter Button */}
      {filterDefs.length > 0 && onFilterChange && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            onClick={() => { setFilterOpen(true); onFilterOpen?.() }}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '13px',
              height: '38px',
              px: 1.5,
              color: activeCount > 0 ? themeColors.primary : '#64748b',
              borderColor: activeCount > 0 ? themeColors.primary : '#e2e8f0',
              bgcolor: activeCount > 0 ? `${themeColors.primary}08` : '#fff',
              '&:hover': {
                borderColor: themeColors.primary,
                bgcolor: `${themeColors.primary}08`,
              },
            }}
          >
            Filters
            {activeCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  minWidth: 18,
                  height: 18,
                  borderRadius: '9px',
                  bgcolor: themeColors.primary,
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: '4px',
                }}
              >
                {activeCount}
              </Box>
            )}
          </Button>

          {activeCount > 0 && (
            <Tooltip title="Reset Filters">
              <IconButton
                size="small"
                onClick={() => onFilterChange(EMPTY_UNIFIED_FILTERS)}
                sx={{
                  color: '#64748b',
                  '&:hover': {
                    color: themeColors.primary,
                    borderColor: themeColors.primary,
                    bgcolor: `${themeColors.primary}08`,
                  },
                }}
              >
                <FilterAltOffIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <UnifiedFilterDialog
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            onApply={onFilterChange}
            filterDefs={filterDefs}
            initialFilters={filters!}
          />
        </Box>
      )}

      {/* Data refresh — right of the Filters button */}
      {onRefresh && (
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={isRefreshing}
              sx={{
                height: '38px',
                width: '38px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                bgcolor: '#fff',
                color: '#64748b',
                '&:hover': {
                  color: themeColors.primary,
                  borderColor: themeColors.primary,
                  bgcolor: `${themeColors.primary}08`,
                },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  ...(isRefreshing && {
                    animation: 'listToolbarSpin 0.8s linear infinite',
                    '@keyframes listToolbarSpin': {
                      from: { transform: 'rotate(0deg)' },
                      to:   { transform: 'rotate(360deg)' },
                    },
                  }),
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {leftExtra}

      {/* Spacer to push view mode and extra actions to the right */}
      <Box sx={{ flex: 1 }} />

      {/* Selection Actions */}
      {selectedCount !== undefined && selectedCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            mr: 1.5,
            bgcolor: themeColors.background,
            border: '1px solid #C4B5FD',
            borderRadius: '8px',
            height: '40px',
            color: themeColors.primaryDark
          }}
        >
          <Typography fontSize="12px" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            {selectedCount} selected
          </Typography>
          {bulkActions?.map((action, idx) => (
            <Button
              key={idx}
              size="small"
              variant="outlined"
              color={action.color === 'error' ? 'error' : 'primary'}
              onClick={action.onClick}
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'none',
                height: 28,
                minWidth: 'auto',
                px: 1,
                ...(action.color !== 'error' ? {
                  borderColor: '#7C3AED',
                  color: '#7C3AED',
                  '&:hover': { bgcolor: '#EDE9FE', borderColor: '#6D28D9' },
                } : {})
              }}
            >
              {action.label}
            </Button>
          ))}
          {onClearSelection && (
            <Button
              size="small"
              onClick={onClearSelection}
              sx={{
                fontSize: '11px',
                color: '#6B7280',
                textTransform: 'none',
                height: 28,
                minWidth: 'auto',
                px: 1,
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}

      {/* View Mode Toggle */}
      {viewMode && onViewModeChange && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${themeColors.border}`,
            borderRadius: '8px',
            bgcolor: '#f8fafc',
            p: 0.5,
            gap: 0.5,
            height: '40px',
          }}
        >
          <Button
            size="small"
            onClick={() => onViewModeChange('table')}
            startIcon={<TableIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
              px: 2,
              height: '100%',
              minWidth: 90,
              border: '1px solid transparent',
              ...(viewMode === 'table'
                ? {
                  background: '#fff',
                  color: themeColors.primary,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  borderColor: '#e2e8f0',
                  '&:hover': { background: '#fff' },
                }
                : {
                  color: '#4b5563',
                  bgcolor: 'transparent',
                  '&:hover': { borderColor: themeColors.primaryDark, bgcolor: 'transparent' },
                }),
            }}
          >
            Table
          </Button>
          <Button
            size="small"
            onClick={() => onViewModeChange('kanban')}
            startIcon={<KanbanIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
              px: 2,
              height: '100%',
              minWidth: 90,
              border: '1px solid transparent',
              ...(viewMode === 'kanban'
                ? {
                  background: '#fff',
                  color: themeColors.primary,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  borderColor: '#e2e8f0',
                  '&:hover': { background: '#fff' },
                }
                : {
                  color: '#4b5563',
                  bgcolor: 'transparent',
                  '&:hover': { borderColor: themeColors.primaryDark, bgcolor: 'transparent' },
                }),
            }}
          >
            Kanban
          </Button>
        </Box>
      )}

      {/* Extra Actions */}
      {extraActions}
    </Box>
  )
}
